
import { AlertCircle, History } from 'lucide-react';
import type { ReactNode } from 'react';

import type { ApiError } from '../../api/client';
import { ErrorBanner } from '../../components/ErrorBanner';
import { JobStatusBadge } from '../../components/StatusBadge';
import { Card, CardHead, EmptyState, SkeletonRows } from '../../components/ui';
import { authorLabel } from '../../lib/author';
import type { Id, Page, TtsRun } from '../../types/api';
import styles from './TtsPage.module.css';
import { formatDateTime, formatDuration, languageLabel, plural } from './format';

interface Props {
  data: Page<TtsRun> | undefined;
  isPending: boolean;
  error: ApiError | null;
  onRetry: () => void;
  activeRunId: Id | null;
  onOpen: (run: TtsRun) => void;
  voiceName: (voiceId: string) => string;
  filter?: ReactNode;
}

export function TtsHistory({
  data,
  isPending,
  error,
  onRetry,
  activeRunId,
  onOpen,
  voiceName,
  filter,
}: Props) {
  const items = data?.items ?? [];
  const totalDurationMs = items.reduce((sum, run) => sum + run.total_duration_ms, 0);

  return (
    <Card>
      <CardHead
        title={<span className="eyebrow">история генераций</span>}
        actions={
          <>
            {filter}
            {data ? (
              <span className={styles.counterRight}>
                {data.total} {plural(data.total, 'прогон', 'прогона', 'прогонов')} ·{' '}
                {formatDuration(totalDurationMs)} на этой странице
              </span>
            ) : null}
          </>
        }
      />

      {isPending && (
        <div className={styles.pad}>
          <SkeletonRows rows={3} />
        </div>
      )}

      {error && (
        <div className={styles.pad}>
          <ErrorBanner error={error} onRetry={onRetry} />
        </div>
      )}

      {!isPending && !error && items.length === 0 && (
        <EmptyState
          compact
          icon={<History size={17} strokeWidth={1.5} />}
          title="Здесь появится всё, что вы озвучили: текст, голос, длительность."
        />
      )}

      {items.map((run) => (
        <button
          key={run.run_id}
          type="button"
          className={run.run_id === activeRunId ? styles.historyRowActive : styles.historyRow}
          onClick={() => onOpen(run)}
          title={run.error_message ?? run.title}
        >
          <span className={styles.historyTitle}>{run.title || 'Без названия'}</span>
          <span className={styles.historyAuthor} title="Кто озвучил">
            {authorLabel(run.author)}
          </span>
          <span className={styles.historyVoice}>{voiceName(run.voice_id)}</span>
          <span className={styles.historyLang}>{languageLabel(run.language)}</span>
          <span className={styles.historySeg}>
            {run.segment_count} {plural(run.segment_count, 'сегм.', 'сегм.', 'сегм.')}
          </span>
          <span className={styles.historyDur}>{formatDuration(run.total_duration_ms)}</span>
          <span className={styles.historyDate}>{formatDateTime(run.created_at)}</span>
          {run.status !== 'completed' && <JobStatusBadge status={run.status} />}
          {run.error_message && (
            <AlertCircle size={13} strokeWidth={1.5} className={styles.errorIcon} />
          )}
        </button>
      ))}
    </Card>
  );
}
